import { useEffect, useState } from 'react';
import { Box, Card, CardContent, Chip, LinearProgress, Skeleton, Stack, Typography } from '@mui/material';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { SvgIconComponent } from '@mui/icons-material';
import { useGetIdentity } from '@refinedev/core';
import { getDashboardStats, type DashboardStats } from '../lib/dashboard-api';
import { getBillingSummary, currentMonth, type BillingSummary } from '../lib/ops-api';
import { formatRupiah } from '../lib/format';
import { useT } from '../i18n';

interface Identity {
  id: string;
  name: string;
  email: string;
  role: 'STAFF' | 'SECURITY' | 'RESIDENT';
  permissions: string[];
  cluster_id: string;
}

type Attention = 'warning' | 'error' | undefined;
interface Tile {
  title: string;
  key: keyof DashboardStats;
  icon: SvgIconComponent;
  attention?: Attention;
}

// `title` holds an i18n key, rendered via t().
const TILES: Tile[] = [
  { title: 'dash.tileUnits', key: 'units', icon: ApartmentIcon },
  { title: 'dash.tileResidents', key: 'residents', icon: PeopleIcon },
  { title: 'dash.tileOpenInvoices', key: 'openInvoices', icon: ReceiptLongIcon, attention: 'warning' },
  { title: 'dash.tilePendingBookings', key: 'pendingBookings', icon: EventAvailableIcon },
  { title: 'dash.tileOpenComplaints', key: 'openComplaints', icon: ReportProblemIcon, attention: 'warning' },
  { title: 'dash.tileActiveEmergencies', key: 'activeEmergencies', icon: WarningAmberIcon, attention: 'error' },
];

function greetingKey(): string {
  const h = new Date().getHours();
  if (h < 11) return 'dash.greetingMorning';
  if (h < 18) return 'dash.greetingAfternoon';
  return 'dash.greetingEvening';
}

export function DashboardPage() {
  const { data: identity, isLoading } = useGetIdentity<Identity>();
  const t = useT();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [summary, setSummary] = useState<BillingSummary | null>(null);

  useEffect(() => {
    let active = true;
    getDashboardStats()
      .then((s) => { if (active) setStats(s); })
      .catch(() => {})
      .finally(() => { if (active) setStatsLoading(false); });
    getBillingSummary(currentMonth())
      .then((s) => { if (active) setSummary(s); })
      .catch(() => {}); // current-month collection; fault-tolerant (panel hidden if this fails)
    return () => { active = false; };
  }, []);

  const pct = summary ? Math.round(summary.collectionRate * 100) : 0;

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box>
          <Typography variant="h4">
            {t(greetingKey())}{identity?.name ? `, ${identity.name}.` : '.'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('dash.subtitle')}
          </Typography>
        </Box>
        {isLoading ? (
          <Skeleton variant="text" width={160} />
        ) : identity ? (
          <Stack alignItems="flex-end" spacing={0.5}>
            <Typography variant="body2" color="text.secondary">{t('dash.signedInAs', { name: identity.name })}</Typography>
            <Stack direction="row" spacing={1}>
              <Chip label={identity.role} size="small" color="primary" variant="outlined" />
            </Stack>
          </Stack>
        ) : null}
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
        {TILES.map((tile) => {
          const value = stats ? stats[tile.key] : null;
          const active = typeof value === 'number' && value > 0 ? tile.attention : undefined;
          const tileBg = 'action.hover';
          const iconColor = active === 'error' ? 'error.main' : active === 'warning' ? 'warning.main' : 'secondary.main';
          const numColor = active === 'error' ? 'error.main' : active === 'warning' ? 'warning.main' : 'text.primary';
          const Icon = tile.icon;
          return (
            <Card
              key={tile.title}
              variant="outlined"
              sx={{ transition: 'box-shadow 200ms ease, transform 200ms ease', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 2px 4px rgba(28,27,25,0.06), 0 12px 26px rgba(28,27,25,0.08)' } }}
            >
              <CardContent>
                <Box sx={{ width: 36, height: 36, borderRadius: 2.5, bgcolor: tileBg, color: iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                  <Icon fontSize="small" />
                </Box>
                <Typography variant="body2" color="text.secondary">{t(tile.title)}</Typography>
                <Typography variant="h4" sx={{ mt: 0.5, color: numColor, fontVariantNumeric: 'tabular-nums' }}>
                  {statsLoading ? <Skeleton variant="text" width={48} /> : value ?? '—'}
                </Typography>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {summary ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
              {t('dash.collectionThisMonth')}
            </Typography>

            {/* Two readings of the same month: how much money came in, and how
                many houses are square. */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 1.5, sm: 4 }}
              sx={{ mb: 1.5 }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('dash.amountCollected')}
                </Typography>
                <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }}>
                  Rp {formatRupiah(summary.collectedCents)}
                  <Typography component="span" variant="body2" color="text.secondary">
                    {' / '}{formatRupiah(summary.collectedCents + summary.outstandingCents)}
                  </Typography>
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('dash.housesPaid')}
                </Typography>
                <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }}>
                  {summary.unitsBilled > 0 ? summary.unitsPaid : '—'}
                  <Typography component="span" variant="body2" color="text.secondary">
                    {' / '}{summary.unitsBilled}
                  </Typography>
                </Typography>
              </Box>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={pct}
              sx={{ height: 9, borderRadius: 5, bgcolor: 'action.hover', '& .MuiLinearProgress-bar': { borderRadius: 5, bgcolor: 'secondary.main' } }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block', fontVariantNumeric: 'tabular-nums' }}>
              {t('dash.collectedOutstanding', { pct, outstanding: formatRupiah(summary.outstandingCents) })}
            </Typography>
          </CardContent>
        </Card>
      ) : null}
    </Stack>
  );
}
